import axios, { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import ApiClient from 'api/ApiClient';
import { RootState } from 'store';
import { RootAction } from 'actions/ActionTypes';
import { showErrorThunk } from '@spike/notifications-action';
import { ImpersonationTokenDto } from './ImpersonationDtos';

const impersonateUrl = (userId: number): string => `organizations/impersonate/${userId}`;

// Same window name used by the admin app, so the marketplace tab is reused between impersonations.
const marketplaceWindowName = 'cuddles_marketplace';

const HTTP_STATUS_403_FORBIDDEN = 403;

const impersonateMarketplaceUrl = process.env.REACT_APP_IMPERSONATE_MARKETPLACE_URL;

const buildImpersonateUrl = (marketplaceUrl: string, marketplaceId: number, temporalToken: string): string =>
    // source=organization lets the marketplace show the "logged in as" banner, which admin impersonations don't get.
    `${marketplaceUrl}/${marketplaceId}/${temporalToken}?source=organization`;

/**
 * Opens the marketplace app in a new tab, logged in as the given staff user.
 * Resolves to true when the marketplace tab was opened.
 */
export const impersonateStaffThunk = (
    userId: number,
    marketplaceId: number
): ThunkAction<Promise<boolean>, RootState, null, RootAction> => {
    return async dispatch => {
        // Without it the tab would open a relative "undefined/..." path on the portal itself.
        if (!impersonateMarketplaceUrl) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch(showErrorThunk('The marketplace URL is not configured.') as any);
            return false;
        }

        // The tab is opened before any await so it stays inside the click gesture and is not popup-blocked.
        const marketplaceWindow = window.open('', marketplaceWindowName);

        if (!marketplaceWindow) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch(showErrorThunk('Unable to open a new tab. Please allow pop-ups for this site.') as any);
            return false;
        }

        marketplaceWindow.opener = null;

        try {
            const { data }: AxiosResponse<ImpersonationTokenDto> = await ApiClient.post(impersonateUrl(userId), {
                marketplace_id: marketplaceId
            });

            marketplaceWindow.location.href = buildImpersonateUrl(
                impersonateMarketplaceUrl,
                marketplaceId,
                data.token_temporal
            );
            marketplaceWindow.focus();

            return true;
        } catch (apiError: unknown) {
            marketplaceWindow.close();

            // The backend answers 403 (not 401) when the staff member is out of reach, so the org user stays logged in.
            const isForbidden = axios.isAxiosError(apiError) && apiError.response?.status === HTTP_STATUS_403_FORBIDDEN;

            dispatch(
                showErrorThunk(
                    isForbidden
                        ? "You don't have access to log in as this staff member."
                        : 'Unable to log in as this staff member.'
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ) as any
            );

            return false;
        }
    };
};
